import { useRunOnce } from 'fitify-ui/src/hooks/useRunOnce'
import {
  type FocusEvent,
  type InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  type ControllerProps,
  type FieldValues,
  useController,
} from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'

import { useDeviceOSDetect } from '../../../hooks/useDeviceOSDetect'
import { emitInputEvent, FitifyInputEvent } from '../../../utils/form-events'

export type InputFieldProps = InputHTMLAttributes<HTMLInputElement>

export type UseInputFieldProps<TFieldValues extends FieldValues = FieldValues> =
  {
    autoFocus?: InputFieldProps['autoFocus']
    isDisabled?: boolean
    propsOnBlur?: InputFieldProps['onBlur']
  } & Pick<ControllerProps<TFieldValues>, 'control' | 'name'>

export const useInputField = <TFieldValues extends FieldValues = FieldValues>({
  autoFocus,
  control,
  isDisabled,
  name,
  propsOnBlur,
}: UseInputFieldProps<TFieldValues>) => {
  const { isIOS, isAndroid, isDetermined } = useDeviceOSDetect()
  const [uuid] = useState(uuidv4())
  const {
    field: { onBlur, onChange, value, ref: registerRef },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
  })
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    emitInputEvent({ inputUuid: uuid, event: FitifyInputEvent.Mount })
    return () => {
      emitInputEvent({ inputUuid: uuid, event: FitifyInputEvent.Unmount })
    }
  }, [uuid])

  useEffect(() => {
    registerRef(inputRef.current)
  }, [registerRef])

  const handleClick = () => {
    if (!isDisabled && !isFocused) {
      inputRef.current?.focus()
      setIsFocused(true)
    }
  }

  // Auto focus input only for non iOS/Android devices to avoid keyboard pop-up issues
  useRunOnce(
    handleClick,
    Boolean(autoFocus && isDetermined && !isIOS && !isAndroid)
  )

  const handleFocus = () => {
    emitInputEvent({ inputUuid: uuid, event: FitifyInputEvent.Focus })
    setIsFocused(true)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    emitInputEvent({ inputUuid: uuid, event: FitifyInputEvent.Blur })
    onBlur()
    propsOnBlur?.(event)
    setIsFocused(false)
  }

  return {
    error,
    handleBlur,
    handleClick,
    handleFocus,
    inputRef,
    isFocused,
    onChange,
    value,
  }
}
